// CSR-953 — shared presentational markup for a Physical Damage vehicle
// section (POWER UNITS / TRAILERS) and its rows. Extracted from
// `PhysicalDamageVehicleList` so the read-only per-tier card
// (`PhysicalDamageTierCard`) renders identical rows without duplicating the
// markup. The list keeps its existing data-testids byte-for-byte.

import { formatDollar } from "@/lib/formatters";
import type { PolicyVehicle } from "@shared/schema";

export interface VehicleSectionProps {
  title: string;
  rowLabel: string;
  vehicles: PolicyVehicle[];
  total: number;
  testId: string;
  rowTestIdPrefix?: string;
}

export function VehicleSection({
  title,
  rowLabel,
  vehicles,
  total,
  testId,
  rowTestIdPrefix = "physical-damage-vehicle-row",
}: VehicleSectionProps) {
  return (
    <section data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
          {title}
        </h4>
        <span className="text-xs font-medium text-muted-foreground">
          {vehicles.length} of {total}
        </span>
      </div>
      {vehicles.length === 0 ? (
        <p className="text-sm italic text-muted-foreground">None selected.</p>
      ) : (
        <div className="space-y-1">
          {vehicles.map((vehicle, i) => (
            <VehicleRow
              key={vehicle.vin || `${testId}-${i}`}
              label={rowLabel}
              vehicle={vehicle}
              testId={`${rowTestIdPrefix}-${vehicle.vin || i}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

interface VehicleRowProps {
  label: string;
  vehicle: PolicyVehicle;
  testId: string;
}

function VehicleRow({ label, vehicle, testId }: VehicleRowProps) {
  const vehicleDescription = [vehicle.year, vehicle.make, vehicle.model]
    .map((part) => (part == null ? "" : String(part).trim()))
    .filter(Boolean)
    .join(" ");
  const formattedValue =
    vehicle.value && vehicle.value.trim() !== "" ? formatDollar(vehicle.value) : "";
  const segments = [
    vehicle.vin ? `VIN ${vehicle.vin}` : "",
    vehicleDescription,
    vehicle.truckType ?? "",
    formattedValue,
  ].filter((segment) => segment && segment.trim() !== "");

  return (
    <div className="flex items-baseline gap-3 text-sm" data-testid={testId}>
      <span className="text-xs text-muted-foreground shrink-0 w-20">{label}</span>
      <span className="min-w-0 text-foreground break-words">
        {segments.join(" · ")}
      </span>
    </div>
  );
}
